import React, { forwardRef, ReactElement, useEffect, useMemo, useState } from 'react'

import { clsx } from 'clsx'
import { useAtom, useAtomValue } from 'jotai'

import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'

import { CompoundRouteHeader } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/CompoundRouteHeader'
import { JourneyType } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/JourneyType'
import { RouteHeader } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/RouteHeader'
import { Filters } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/Filters'
import { PriceFilter } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/PriceSwitch'
import { RouteFilter } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/RouteFilters'
import { TimeFilter } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/TimeFilters'
import { Collapse } from '@/components/shared/Collapse'
import { SelectedDepartures } from '@/states/selection.interface'
import { selectedDeparturesAtom, selectedRoutesAtom } from '@/states/selectionAtom'

import { isCompoundRoute, isCompoundRouteUsingRoute } from '@/utils/routeHelpers'

import { useTranslation } from '@/hooks/useDebugTranslations'

import { DepartureFunctions } from '../../YourBookingHandlers'
import DepartureRow from '../departure-row/DepartureRow'
import { DepartureNoSailing } from './DeparturesNoSailing'
import ViewHideDeparturesLink from './ViewHideDeparturesLink'

export const showDeparture = (
  returnLeg: boolean | undefined,
  showDepartures: boolean,
  selectedDepartures: SelectedDepartures,
  departure: BffInterfacesDeparture.Departure
): boolean => {
  if (showDepartures) return true
  if (returnLeg) {
    const arrivalTime = selectedDepartures?.departureOut?.arrivalDateTime
    if (departure.departureDate !== selectedDepartures?.departureOut?.departureDate) return true
    if (arrivalTime && arrivalTime >= departure.checkinDateTime) return false
  } else {
    const checkinTime = selectedDepartures?.departureHome?.checkinDateTime
    if (checkinTime && checkinTime < departure.arrivalDateTime) return false
  }
  return true
}

// cheapest ticket
const getMinPrice = (departure: BffInterfacesDeparture.Departure) => Math.min(...departure.tickets.map((t) => t.price))

export const applyPriceFilter = (departures: BffInterfacesDeparture.Departure[], selectedPriceFilter: string) => {
  if (selectedPriceFilter === 'TIME') {
    return departures
  }

  const withPrice = departures.filter((d) => d.tickets?.length)

  if (selectedPriceFilter === 'PRICE_LOW') {
    return [...withPrice].sort((a, b) => getMinPrice(a) - getMinPrice(b))
  }

  if (selectedPriceFilter === 'PRICE_HIGH') {
    return [...withPrice].sort((a, b) => getMinPrice(b) - getMinPrice(a))
  }

  return departures
}

export const departureIsWithinTimeFilter = (departureTime: string, selectedDepartureTimeFilters: TimeFilter): boolean => {
  if (selectedDepartureTimeFilters.all) return true
  const hour = Number(departureTime.split(':')[0])
  if (selectedDepartureTimeFilters.night && 0 <= hour && hour < 6) return true
  if (selectedDepartureTimeFilters.morning && 6 <= hour && hour < 12) return true
  if (selectedDepartureTimeFilters.afternoon && 12 <= hour && hour < 18) return true
  if (selectedDepartureTimeFilters.evening && 18 <= hour && hour < 24) return true
  return false
}

interface Props {
  dateChanged: (date: string, legNumber: 1 | 2) => void
  departureClicked: DepartureFunctions['DepartureClicked']
  departureDate: string
  departures?: BffInterfacesDeparture.Departure[]
  editElement?: ReactElement
  legType: string
  loading: boolean
  onOutboundAccordionToggle: (isOpen: boolean) => void
  onReturnAccordionToggle: (isOpen: boolean) => void
  outboundAccordionOpen: boolean
  returnAccordionOpen: boolean
  returnLeg?: boolean
  selected?: boolean
  selectedDepartureId?: string
  selectedRouteFilters: RouteFilter
  selectedType?: string
  setSelectedRouteFilters: (filters: RouteFilter) => void
  switchElement?: ReactElement
}

const mapper = (d: BffInterfacesDeparture.Departure) => {
  const departureTimeFormatted = d.departureTime?.match(/..:../)?.[0]
  const arrivalTimeFormatted = d.arrivalTime?.match(/..:../)?.[0]
  const checkInTimeFormatted = d.checkinTime?.match(/..:../)?.[0]
  return { ...d, arrivalTimeFormatted, checkInTimeFormatted, departureTimeFormatted }
}

const DepartureLeg = forwardRef<HTMLDivElement, Props>(
  (
    {
      dateChanged,
      departureClicked,
      departureDate,
      departures,
      editElement,
      legType,
      loading,
      onOutboundAccordionToggle,
      onReturnAccordionToggle,
      outboundAccordionOpen,
      returnAccordionOpen,
      returnLeg,
      selectedDepartureId,
      selectedRouteFilters,
      selectedType,
      setSelectedRouteFilters,
      switchElement,
    }: Props,
    ref
  ): React.ReactElement => {
    const [selectedTimeFilters, setSelectedTimeFilters] = useState<TimeFilter>({
      afternoon: false,
      all: true,
      evening: false,
      morning: false,
      night: false,
    })
    const [selectedPriceFilter, _setSelctedPriceFilter] = useState<PriceFilter>('TIME')
    const [selectedRoutes, _setSelectedRoutes] = useAtom(selectedRoutesAtom)
    const departuresMapped = useMemo(() => departures?.map(mapper), [departures])
    const selectedDepartures = useAtomValue(selectedDeparturesAtom)
    const [departureIdRowOpen, setDepartureIdRowOpen] = useState<string>()
    const [showDepartures, setShowDepartures] = useState<boolean>(false)
    const [filtering, setFiltering] = useState<boolean>(false)

    const { t } = useTranslation()
    const isRouteActive = (departurePortCode?: string, arrivalPortCode?: string): boolean => {
      if (!isCompoundRoute(departurePortCode, arrivalPortCode)) return true
      if (returnLeg && (departurePortCode === 'FRCQF' || arrivalPortCode === 'FRCQF') && selectedRouteFilters.frcqf) return true
      if (returnLeg && (departurePortCode === 'FRDKK' || arrivalPortCode === 'FRDKK') && selectedRouteFilters.frdkk) return true
      if (!returnLeg && (arrivalPortCode === 'FRCQF' || departurePortCode === 'FRCQF') && selectedRouteFilters.frcqf) return true
      if (!returnLeg && (arrivalPortCode === 'FRDKK' || departurePortCode === 'FRDKK') && selectedRouteFilters.frdkk) return true
      return false
    }

    // For animating blinking when filtering
    useEffect(() => {
      setTimeout(() => {
        setFiltering(false)
      }, 200)
    }, [selectedTimeFilters])

    let indexForViewHideDeparturesLink: any

    if (returnLeg) {
      const arrivalDateTime = selectedDepartures?.departureOut?.arrivalDateTime
      indexForViewHideDeparturesLink = arrivalDateTime && departuresMapped?.findIndex((x) => x.checkinDateTime > arrivalDateTime)
    } else {
      const checkinDateTime = selectedDepartures?.departureHome?.checkinDateTime
      indexForViewHideDeparturesLink = checkinDateTime && departuresMapped?.findIndex((x) => x.arrivalDateTime > checkinDateTime)
    }

    const filterDepartures = (departure: any) => {
      const departureVisible =
        (departureIsWithinTimeFilter(departure.departureTime, selectedTimeFilters) &&
          isRouteActive(departure.departurePortCode, departure.arrivalPortCode)) ||
        false
      if (departureVisible) return departure
    }

    const hasDeparturesShown = departuresMapped?.some((d) => filterDepartures(d))

    return (
      <div
        className="bg-semantic-global-background-light-grey"
        data-e2e-leg={legType.toLowerCase()}
        data-e2e-leg-loading={loading}
        id={`${legType}DepartureLeg`}
        ref={ref}
      >
        <div className="mb-semantic-global-md md:mb-semantic-global-lg">
          {isCompoundRouteUsingRoute(selectedRoutes.outbound) ? (
            <CompoundRouteHeader
              editElement={editElement}
              isOpen={returnLeg ? returnAccordionOpen : outboundAccordionOpen}
              returnLeg={returnLeg}
              selectedRouteFilters={selectedRouteFilters}
            />
          ) : (
            <RouteHeader editElement={editElement} isOpen={returnLeg ? returnAccordionOpen : outboundAccordionOpen} returnLeg={returnLeg} />
          )}

          <Collapse
            isOpen={Boolean(legType && (returnLeg ? returnAccordionOpen && selectedDepartures.departureOut : outboundAccordionOpen))}
          >
            <Filters
              dateChanged={dateChanged}
              departureDate={departureDate}
              onRouteFilterChange={(srf) => setSelectedRouteFilters(srf)}
              onTimeFilterChange={(dtf) => {
                setFiltering(true)
                setSelectedTimeFilters(dtf)
              }}
              returnLeg={returnLeg}
              selectedRouteFilters={selectedRouteFilters}
              selectedTimeFilters={selectedTimeFilters}
            />
          </Collapse>
        </div>

        <ul aria-label={returnLeg ? 'Return departures' : 'Outbound departures'} className="flex flex-col" data-e2e="departures">
          {departuresMapped && departuresMapped.length > 0
            ? applyPriceFilter(
                departuresMapped
                  .filter((d) => filterDepartures(d))
                  .filter(() => {
                    if (returnLeg && !selectedDepartures.departureOut) return false
                    return true
                  }),
                selectedPriceFilter
              ).map((d, i) => {
                const isAccordionOpen = returnLeg ? returnAccordionOpen : outboundAccordionOpen
                const isSelected = selectedDepartureId === d.departureId
                const departureVisible = showDeparture(returnLeg, showDepartures, selectedDepartures, d)
                const showRow = isAccordionOpen
                  ? departureVisible || indexForViewHideDeparturesLink === -1 || indexForViewHideDeparturesLink === 0
                  : isSelected

                return (
                  <div
                    className={clsx('transition-opacity duration-500 ease-in-out', filtering && 'opacity-30 md:opacity-50')}
                    key={`${d.departureTime}-${d.departurePortCode}-${d.arrivalPortCode}`}
                  >
                    <Collapse isOpen={showRow}>
                      <DepartureRow
                        accordionOpen={isAccordionOpen}
                        active={
                          (departureIsWithinTimeFilter(d.departureTime, selectedTimeFilters) &&
                            isRouteActive(d.departurePortCode, d.arrivalPortCode)) ||
                          false
                        }
                        arrivalDateTime={d.arrivalDateTime}
                        arrivalPortCode={d.arrivalPortCode}
                        arrivalPortName={t(d.arrivalPortCode)}
                        arrivalTime={d.arrivalTime}
                        checkinDateTime={d.checkinDateTime}
                        checkinTime={d.checkinTime}
                        departureClicked={departureClicked}
                        departureId={d.departureId}
                        departurePortCode={d.departurePortCode}
                        departurePortName={t(d.departurePortCode)}
                        departureTime={d.departureTime}
                        departureTimeFormatted={d.departureTime?.match(/..:../)?.[0]}
                        departureVisible={departureIsWithinTimeFilter(d.departureTime, selectedTimeFilters)}
                        isOpen={departureIdRowOpen === d.departureId}
                        loading={loading}
                        onDepartureRowClicked={(departureId: string) => setDepartureIdRowOpen(departureId)}
                        onEditClicked={() => {
                          returnLeg ? onReturnAccordionToggle(true) : onOutboundAccordionToggle(true)
                        }}
                        returnLeg={returnLeg}
                        selected={isSelected}
                        selectedType={selectedType}
                        tickets={d.tickets}
                        tripDuration={d.tripDuration}
                      />
                    </Collapse>
                    {indexForViewHideDeparturesLink === i + 1 && isAccordionOpen ? (
                      <ViewHideDeparturesLink returnLeg={returnLeg} setShowDepartures={setShowDepartures} showDepartures={showDepartures} />
                    ) : null}
                  </div>
                )
              })
            : null}
          {!hasDeparturesShown && (returnLeg ? returnAccordionOpen && selectedDepartures.departureOut : outboundAccordionOpen) ? (
            <DepartureNoSailing />
          ) : null}
        </ul>
        <JourneyType switchElement={switchElement} />
      </div>
    )
  }
)

DepartureLeg.displayName = 'Leg'

export default DepartureLeg
